<div class="flex min-w-0 flex-1 flex-col">
  <!-- Slot for things like breadcrumbs -->
  <ng-content select="[before-table]"></ng-content>

  <div class="flex-1">
    <aura-data-table
      matSort
      [inputColumns]="columns()"
      [filters]="filters()"
      [items]="items()"
      [count]="count()"
      [loading]="isLoading()"
      [pagination]="pagination()"
      [topButtons]="topButtons()"
      [itemActions]="itemActions()"
      [mappedFilters]="mappedFilters()"
      [rowClickable]="rowClickable()"
      [hideSearch]="hideSearch()"
      [searchPlaceHolder]="searchPlaceHolder()"
      (removeFilter)="removeFilter()?.($event)"
      (clearFiltering)="clearFiltering()?.()"
      (triggerRefresh)="triggerRefresh()?.()"
      (triggerFiltering)="triggerFiltering()?.()"
      (filterViewChanged)="filterViewChanged()?.($event)"
      (itemClicked)="itemClicked()?.($event)"
      (pageChanged)="pageChanged()?.($event)"
      (searchChanged)="searchChanged()?.($event)"
      (matSortChange)="sortChanged()?.($event)"
    >
      <ng-template #filterTemplate>
        <etxe-properties-data-table-filter
          [dto]="appliedFilter()"
          [filters]="filters()"
          [currentFilterView]="currentFilterView()"
          (filterChanged)="filterChanged()?.($event)"
        />
      </ng-template>
      @for (column of columns(); track $index) {
        @if (!column.native) {
          <ng-container [matColumnDef]="column.key">
            <th
              mat-header-cell
              *matHeaderCellDef
              mat-sort-header
              [sortActionDescription]="'Trier par ' + column.label | lowercase"
              class="font-bold! text-indigo-11!"
            >
              {{ column.label }}
            </th>
            @switch (column.key) {
              @case ('annonce') {
                <td mat-cell *matCellDef="let property" class="py-4">
                  <div class="flex items-center gap-4 cursor-pointer" [etxePropertyHoverPreview]="property">
                    <div
                      class="h-16 w-16 overflow-hidden rounded-lg bg-gray-2 shadow-sm border border-gray-6"
                    >
                      @if (property.media?.coverPhoto?.url) {
                        <img
                          [src]="property.media.coverPhoto.url"
                          alt="Photo de couverture"
                          class="h-full w-full object-cover"
                        />
                      } @else {
                        <div class="flex h-full w-full items-center justify-center text-gray-9">
                          <mat-icon>home</mat-icon>
                        </div>
                      }
                    </div>
                    <span class="font-medium text-gray-12">{{ property.presentation?.title }}</span>
                  </div>
                </td>
              }
              @case ('owner') {
                <td mat-cell *matCellDef="let property">
                  @if (property.owner?.identity; as identity) {
                    <button
                      [routerLink]="['/users', property.owner._id]"
                      mat-button
                      class="link flex flex-row items-center"
                    >
                      <aura-avatar
                        [label]="identity.firstName"
                        [srcImage]="$safeNavigationMigration(identity.avatar?.url)"
                        [size]="'xs'"
                      />

                      <div class="ml-2">
                        {{ identity.firstName | titlecase }}
                        {{ identity.lastName | titlecase }}
                      </div>
                    </button>
                  } @else {
                    <span class="italic text-gray-9 text-xs">Propriétaire inconnu</span>
                  }
                </td>
              }
              @case ('definition.reference') {
                <td mat-cell *matCellDef="let property">
                  @if (property.definition?.reference; as reference) {
                    <span
                      class="font-mono font-medium text-gray-11 bg-gray-2 px-2 py-0.5 rounded border border-gray-5"
                    >
                      {{ reference }}
                    </span>
                  } @else {
                    <span class="text-xs text-gray-8 italic">-</span>
                  }
                </td>
              }
              @case ('definition.propertyType') {
                <td mat-cell *matCellDef="let property">
                  {{
                    $safeNavigationMigration(property.definition?.offerType) | etxeEnumDisplay: 'offerType'
                  }}
                  de
                  {{
                    $safeNavigationMigration(property.definition?.propertyType)
                      | etxeEnumDisplay: 'propertyType'
                      | lowercase
                  }}
                </td>
              }
              @case ('location.address.region') {
                <td mat-cell *matCellDef="let property">
                  {{ property.location?.address?.city || ''
                  }}{{ property.location?.address?.region ? ', ' + property.location?.address?.region : '' }}
                </td>
              }
              @case ('verification') {
                <td mat-cell *matCellDef="let property" class="py-3">
                  <div class="flex flex-wrap items-center gap-1.5">
                    <!-- Anti-Bypass Moderation Flag Alert -->
                    @if (hasModerationFlag(property)) {
                      <div
                        class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-ruby-3 text-ruby-11 border border-ruby-6 animate-pulse"
                        matTooltip="Signalement à traiter"
                      >
                        <mat-icon class="text-xs! w-3.5! h-3.5! text-ruby-11">warning</mat-icon>
                        <span>Signalement</span>
                      </div>
                    }

                    <!-- Owner Verification Status -->
                    @switch (getOwnerVerificationStatus(property)) {
                      @case ('verified') {
                        <div
                          class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-jade-3 text-jade-11 border border-jade-6"
                          matTooltip="Propriétaire vérifié"
                        >
                          <span class="h-1.5 w-1.5 rounded-full bg-jade-9"></span>
                          <span>Propriétaire</span>
                        </div>
                      }
                      @case ('rejected') {
                        <div
                          class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-ruby-3 text-ruby-11 border border-ruby-6"
                          matTooltip="Vérification propriétaire refusée"
                        >
                          <span class="h-1.5 w-1.5 rounded-full bg-ruby-9"></span>
                          <span>Propriétaire refusé</span>
                        </div>
                      }
                      @case ('flagged') {
                        <div
                          class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-3 text-amber-11 border border-amber-6"
                          matTooltip="Propriétaire signalé"
                        >
                          <span class="h-1.5 w-1.5 rounded-full bg-amber-9"></span>
                          <span>Signalé</span>
                        </div>
                      }
                      @default {
                        <div
                          class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-3 text-gray-11 border border-gray-6"
                          matTooltip="Propriétaire non vérifié"
                        >
                          <span class="h-1.5 w-1.5 rounded-full bg-gray-8"></span>
                          <span>Non vérifié</span>
                        </div>
                      }
                    }

                    <!-- Document Verification Status -->
                    @if (hasPendingReviewDocuments(property)) {
                      <div
                        class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-3 text-blue-11 border border-blue-6"
                        matTooltip="Justificatif de propriété déposé, en cours de vérification"
                      >
                        <mat-icon class="text-xs! w-3.5! h-3.5! text-blue-11">schedule</mat-icon>
                        <span>En attente de validation</span>
                      </div>
                    } @else if (
                      getOwnershipDocumentStatus(property) === 'rejected' || hasRejectedDocuments(property)
                    ) {
                      <div
                        class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-ruby-3 text-ruby-11 border border-ruby-6"
                        matTooltip="Justificatif refusé lors de la vérification"
                      >
                        <mat-icon class="text-xs! w-3.5! h-3.5! text-ruby-11">error_outline</mat-icon>
                        <span>Justificatif refusé</span>
                      </div>
                    } @else if (
                      !hasDocuments(property) && getOwnerVerificationStatus(property) !== 'verified'
                    ) {
                      <div
                        class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-2 text-gray-10 border border-gray-5"
                        matTooltip="Aucun justificatif déposé pour le moment"
                      >
                        <mat-icon class="text-xs! w-3.5! h-3.5! text-gray-9">description</mat-icon>
                        <span>Sans document</span>
                      </div>
                    }

                    @if (getDpeVerificationStatus(property); as dpeStatus) {
                      @switch (dpeStatus) {
                        @case ('verified') {
                          <div
                            class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-jade-3 text-jade-11 border border-jade-6"
                            matTooltip="DPE validé"
                          >
                            <span class="h-1.5 w-1.5 rounded-full bg-jade-9"></span>
                            DPE
                          </div>
                        }
                        @case ('rejected') {
                          <div
                            class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-ruby-3 text-ruby-11 border border-ruby-6"
                            matTooltip="DPE refusé"
                          >
                            <span class="h-1.5 w-1.5 rounded-full bg-ruby-9"></span>
                            DPE
                          </div>
                        }
                        @case ('flagged') {
                          <div
                            class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-3 text-amber-11 border border-amber-6"
                            matTooltip="DPE signalé"
                          >
                            <span class="h-1.5 w-1.5 rounded-full bg-amber-9"></span>
                            DPE
                          </div>
                        }
                      }
                    }
                  </div>
                </td>
              }
              @case ('state.status') {
                <td mat-cell *matCellDef="let property" class="w-44">
                  @if (
                    property.state?.status === 'deactivated' &&
                    property.verification?.owner === 'verified' &&
                    !property.state?.firstActivatedAt
                  ) {
                    <div class="flex items-center gap-2">
                      <div class="h-2 w-2 rounded-full bg-indigo-9"></div>
                      <span class="text-sm font-medium text-indigo-11"> Prête à publier </span>
                    </div>
                  } @else {
                    <div class="flex items-center gap-2">
                      <div
                        class="h-2 w-2 rounded-full"
                        [class]="property.state.status === 'active' ? 'bg-jade-9' : 'bg-ruby-9'"
                      ></div>
                      <span class="text-sm text-gray-11">
                        {{ property.state.status | stateStatusDisplay }}
                      </span>
                    </div>
                  }
                </td>
              }
              @case ('definition.offerType') {
                <td mat-cell *matCellDef="let property">
                  {{ property.definition?.offerType | etxeEnumDisplay: 'offerType' }}
                </td>
              }
              @case ('keyInfo.surface.habitable') {
                <td mat-cell *matCellDef="let property">
                  @if (property.keyInfo?.surface?.habitable; as surface) {
                    <div class="flex flex-col">
                      <span class="font-medium text-gray-12">{{ surface | number: '1.0-2' }} m²</span>
                      @if (property.keyInfo?.surface?.ground || property.keyInfo?.surface?.land; as land) {
                        <span class="text-xs text-gray-10"
                          >Terr. {{ land | number: '1.0-0' : 'fr-FR' }} m²</span
                        >
                      }
                    </div>
                  } @else {
                    <span class="text-gray-8 italic text-xs">-</span>
                  }
                </td>
              }
              @case ('financials.sellingPriceCents') {
                <td mat-cell *matCellDef="let property">
                  @if (property.financials?.sellingPriceCents; as priceCents) {
                    <div class="flex flex-col">
                      <span class="font-semibold text-gray-12">
                        {{ priceCents / 100 | number: '1.0-0' : 'fr-FR' }} €
                      </span>
                      @if (property.keyInfo?.surface?.habitable; as surface) {
                        @if (surface > 0) {
                          <span class="text-xs text-gray-10">
                            {{ priceCents / 100 / surface | number: '1.0-0' : 'fr-FR' }} €/m²
                          </span>
                        }
                      }
                    </div>
                  } @else {
                    <span class="text-gray-8 italic text-xs">-</span>
                  }
                </td>
              }
              @case ('marketStatus.state') {
                <td mat-cell *matCellDef="let property">
                  @if (property.marketStatus?.state; as marketState) {
                    @switch (marketState) {
                      @case ('for_sale') {
                        <div
                          class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-indigo-3 text-indigo-11 border border-indigo-6"
                        >
                          <span class="h-1.5 w-1.5 rounded-full bg-indigo-9"></span>
                          À vendre
                        </div>
                      }
                      @case ('under_mandate') {
                        <div
                          class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-violet-3 text-violet-11 border border-violet-6"
                        >
                          <span class="h-1.5 w-1.5 rounded-full bg-violet-9"></span>
                          Sous mandat
                        </div>
                      }
                      @case ('under_offer') {
                        <div
                          class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-3 text-amber-11 border border-amber-6"
                        >
                          <span class="h-1.5 w-1.5 rounded-full bg-amber-9"></span>
                          Offre acceptée
                        </div>
                      }
                      @case ('under_contract') {
                        <div
                          class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-orange-3 text-orange-11 border border-orange-6"
                        >
                          <span class="h-1.5 w-1.5 rounded-full bg-orange-9"></span>
                          Sous compromis
                        </div>
                      }
                      @case ('sold') {
                        <div
                          class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-jade-3 text-jade-11 border border-jade-6"
                        >
                          <span class="h-1.5 w-1.5 rounded-full bg-jade-9"></span>
                          Vendu
                        </div>
                      }
                      @case ('withdrawn') {
                        <div
                          class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-3 text-gray-11 border border-gray-6"
                        >
                          <span class="h-1.5 w-1.5 rounded-full bg-gray-8"></span>
                          Retiré
                        </div>
                      }
                      @default {
                        <span class="text-xs text-gray-11">
                          {{ marketState | etxeEnumDisplay: 'marketState' }}
                        </span>
                      }
                    }
                  } @else {
                    <span class="text-gray-8 italic text-xs">-</span>
                  }
                </td>
              }
              @case ('characteristics.interior.roomCount') {
                <td mat-cell *matCellDef="let property">
                  @if (property.characteristics?.interior?.roomCount; as rooms) {
                    <div class="flex items-center gap-1 text-sm text-gray-12">
                      <span class="font-medium">{{ rooms }} p.</span>
                      @if (property.characteristics?.interior?.bedroomCount; as beds) {
                        <span class="text-xs text-gray-10">({{ beds }} ch.)</span>
                      }
                    </div>
                  } @else {
                    <span class="text-gray-8 italic text-xs">-</span>
                  }
                </td>
              }
              @case ('characteristics.interior.bedroomCount') {
                <td mat-cell *matCellDef="let property">
                  @if (property.characteristics?.interior?.bedroomCount; as beds) {
                    <span class="text-sm text-gray-12 font-medium">{{ beds }} ch.</span>
                  } @else {
                    <span class="text-gray-8 italic text-xs">-</span>
                  }
                </td>
              }
              @case ('dpe.energyClass') {
                <td mat-cell *matCellDef="let property">
                  @if (property.dpe?.energyClass; as energy) {
                    <div class="flex items-center gap-1.5">
                      <span
                        class="inline-flex items-center justify-center font-bold text-xs h-6 w-6 rounded text-white shadow-xs"
                        [style.background-color]="
                          energy === 'A'
                            ? '#00803C'
                            : energy === 'B'
                              ? '#54B345'
                              : energy === 'C'
                                ? '#CED932'
                                : energy === 'D'
                                  ? '#FFF200'
                                  : energy === 'E'
                                    ? '#FCBA08'
                                    : energy === 'F'
                                      ? '#EB8235'
                                      : '#D71D20'
                        "
                        [class.text-black!]="['C', 'D', 'E'].includes(energy)"
                      >
                        {{ energy }}
                      </span>
                      @if (property.dpe?.climateClass; as ges) {
                        <span class="text-xs text-gray-10">GES {{ ges }}</span>
                      }
                    </div>
                  } @else {
                    <span class="text-gray-8 italic text-xs">Vierge</span>
                  }
                </td>
              }
              @case ('location.address.city') {
                <td mat-cell *matCellDef="let property">
                  <div class="flex flex-col">
                    <span class="font-medium text-gray-12">{{
                      property.location?.address?.city || '-'
                    }}</span>
                    @if (property.location?.address?.postalCode; as postalCode) {
                      <span class="text-xs text-gray-10">{{ postalCode }}</span>
                    }
                  </div>
                </td>
              }
              @case ('state.firstActivatedAt') {
                <td mat-cell *matCellDef="let property">
                  @if (property.state?.firstActivatedAt) {
                    {{ property.state.firstActivatedAt | luxonDate: 'humanize' }}
                  } @else {
                    <span class="text-gray-8 italic text-xs">Jamais activée</span>
                  }
                </td>
              }
              @case ('createdAt') {
                <td mat-cell *matCellDef="let property">
                  {{ property.createdAt | luxonDate: 'humanize' }}
                </td>
              }
              @case ('updatedAt') {
                <td mat-cell *matCellDef="let property">
                  {{ property.updatedAt | luxonDate: 'humanize' }}
                </td>
              }
              @default {
                <td mat-cell *matCellDef="let property">
                  {{ property[column.key] }}
                </td>
              }
            }
          </ng-container>
        }
      }
    </aura-data-table>
  </div>
</div>

<ng-template #propertyHoverTemplate let-property>
  <etxe-property-hover-card [property]="property" />
</ng-template>
